import Button from 'components/Basic/Button';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { setDeletePoints, setEnableFacetEdit } from 'store/slices/RoofEditSlice';
import { syncDeletedRoofIndexes } from 'store/slices/ToolSlice';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import { selectIsAnyToolActionActive, TOOL_ACTION } from 'store/selectors/toolSelector';
import { useEffect } from 'react';
import useEditFacetQuery from 'hooks/api/facet/uesEditFacet';
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import { clearToolModeHandlers, registerToolModeHandlers } from 'components/tool/Editor/ToolRegistry';
import { selectIsRecomputingDsm } from 'store/slices/DsmShadingSlice/selectors';

export default function EditFacet() {
	const dispatch = useDispatch<AppDispatch>();
	const { enableEditFacet, editableFacets, invalidFacetIDs, mergeDeletedFacetIds } = useSelector((state: RootState) => state.roofEditData.data);
	const { hadTransition, deletedRoofIndexes } = useSelector((state: RootState) => state.toolNewPostions.data);
	const { uuid, panel, designType } = useSelector((state: RootState) => state.roofData.data);
	const queriesRunningInBackground = useQueriesRunningInBackGround();

	const isAnyToolAction = useSelector(selectIsAnyToolActionActive);
	const isAnyOtherToolAction = !enableEditFacet && isAnyToolAction;
	const isRecomputing = useSelector(selectIsRecomputingDsm);
	const shouldDisable = queriesRunningInBackground || hadTransition || isAnyOtherToolAction || isRecomputing;
	const updateFacetsEditQuery = useEditFacetQuery();
	const editedValidFacetsCount = editableFacets.filter(seg => seg.isEdited).length;
	const hasValidEdits = editedValidFacetsCount > 0 && invalidFacetIDs.length === 0;

	const handleEnableFacet = () => {
		dispatch(setEnableFacetEdit({ isEnable: !enableEditFacet }));
	};

	useEffect(() => {
		// discarding the edits must also bring back the facets removed by merging
		const discardMergeDeletions = () => {
			if (!mergeDeletedFacetIds.length) return;
			dispatch(syncDeletedRoofIndexes({
				deletedRoofIndexes: deletedRoofIndexes.filter(id => !mergeDeletedFacetIds.includes(id)),
			}));
		};

		const cancelHandler = () => {
			dispatch(setEnableFacetEdit({ isEnable: false }));
			discardMergeDeletions();
		};
		const doneHandler = async () => {
			if (!hasValidEdits) {
				dispatch(setEnableFacetEdit({ isEnable: false }));
				discardMergeDeletions();
				return;
			}

			try {
				await updateFacetsEditQuery.mutateAsync(); 
				await dispatch(changePanel({ uuid, panelKey: panel.key, designType })).unwrap();
			} finally {
				dispatch(setEnableFacetEdit({ isEnable: false }));
			}
		};

		registerToolModeHandlers(TOOL_ACTION.EDIT_FACET, {
			cancel: cancelHandler,
			done: doneHandler,
		});

		return () => {
			clearToolModeHandlers(TOOL_ACTION.EDIT_FACET);
		};
	}, [dispatch,updateFacetsEditQuery,hasValidEdits,uuid,panel?.key,designType,mergeDeletedFacetIds,deletedRoofIndexes,]);
	return (
		<>
			<Button 
				style={{
					background: `${enableEditFacet ? 'var(--toolActiveBgColor)' : 'var(--defaultBgColor)'}`,
					color: `${enableEditFacet ? 'var(--primary)' : 'var(--labelValueColor)'}`,
				}}
				id="edit-facet"
				onClick={handleEnableFacet}
				disabled={shouldDisable}
				tooltipContent={enableEditFacet ? '' : 'Edit Facets'}
				className={`${enableEditFacet ? 'active' : ''} btn-tools btn-editfacet`}>
				<svg xmlns="http://www.w3.org/2000/svg" width="22" height="21" viewBox="0 0 24 23" fill="none">
					<path d="M4.66667 20.0086L19.2519 18.0931M2.62963 18.4739V4.60447M21.1259 16.2015V6.95522M21.1259 
					16.2015C20.0909 16.2015 19.2519 17.0084 19.2519 18.0037C19.2519 18.9991 20.0909 19.806 21.1259 
					19.806C22.1609 19.806 23 18.9991 23 18.0037C23 17.0084 22.1609 16.2015 21.1259 16.2015ZM18.8139 
					4.76119L4.66667 2.84331M22.5111 5.23134C22.5111 6.22669 21.6721 7.03358 20.637 7.03358C19.602 
					7.03358 18.763 6.22669 18.763 5.23134C18.763 4.23599 19.602 3.4291 20.637 3.4291C21.6721 3.4291 
					22.5111 4.23599 22.5111 5.23134ZM4.74815 2.80224C4.74815 3.79759 3.9091 4.60448 2.87407 
					4.60448C1.83905 4.60448 1 3.79759 1 2.80224C1 1.80689 1.83905 1 2.87407 1C3.9091 1 4.74815 
					1.80689 4.74815 2.80224ZM4.74815 20.1978C4.74815 21.1931 3.9091 22 2.87407 22C1.83905 22 1 
					21.1931 1 20.1978C1 19.2024 1.83905 18.3955 2.87407 18.3955C3.9091 18.3955 4.74815 19.2024 
					4.74815 20.1978Z" stroke={`${enableEditFacet ? 'var(--primary)' : 'var(--defaultIconColor) '}`} strokeWidth="1.6"/>
				</svg>
			</Button>
		</>
	);

}